import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Box,
  Button,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  SimpleGrid,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useToast,
} from '@chakra-ui/react';
import { yupResolver } from '@hookform/resolvers/yup';
import IconDownload from 'assets/icons/icon-download.svg?react';
import SubmitButton from 'components/ui/SubmitButton';
import UploadFile from 'components/ui/UploadFile';
import config from 'config';
import { useMessages } from 'hooks/useMessages';
import { useState } from 'react';
import { FormProvider, Resolver, SubmitHandler, useForm } from 'react-hook-form';
import * as Yup from 'yup';
import { useCompaniesContext } from '../contexts/CompaniesContext';
import { useExportCompanyMembers } from '../hooks/useExportCompanyMembers';
import { useImportCompanyRecipients } from '../hooks/useImportCompanyRecipients';

export interface FormValues {
  file: any;
}

interface ImportConflict {
  email: string;
  companyName: string;
}

interface ImportSkippedRow {
  line: number;
  email?: string;
  name?: string;
  reason: string;
}

interface ImportSummary {
  create: number;
  update: number;
  remove: number;
  totalRows: number;
}

type ImportFeedback =
  | {
      type: 'conflicts';
      message: string;
      conflicts: ImportConflict[];
    }
  | {
      type: 'skipped';
      summary: ImportSummary;
      skipped: ImportSkippedRow[];
    };

const validationSchema = Yup.object().shape({
  file: Yup.mixed().required(),
});

const defaultValues: FormValues = {
  file: null,
};

const skipReasonLabels: Record<string, string> = {
  invalid_email: 'E-mail inválido ou vazio',
  duplicate_in_spreadsheet: 'E-mail duplicado na planilha',
};

function ImportFeedbackPanel({ feedback }: { feedback: ImportFeedback }) {
  if (feedback.type === 'conflicts') {
    const conflictCount = feedback.conflicts.length;

    return (
      <Box
        mt="20px"
        borderWidth="1px"
        borderColor="red.200"
        borderRadius="md"
        overflow="hidden"
        bg="red.50"
      >
        <Alert status="error" variant="subtle" borderRadius={0} bg="transparent" px="16px" py="14px">
          <AlertIcon alignSelf="start" mt="2px" />
          <Box>
            <AlertTitle fontSize="sm" mb="1">
              Importação cancelada
            </AlertTitle>
            <AlertDescription fontSize="sm">
              {conflictCount === 1
                ? '1 e-mail da planilha já está cadastrado em outra empresa.'
                : `${conflictCount} e-mails da planilha já estão cadastrados em outras empresas.`}{' '}
              Corrija ou remova os registros abaixo, salve a planilha e envie o arquivo
              novamente.
            </AlertDescription>
          </Box>
        </Alert>

        <Box px="16px" pb="16px">
          <Text fontSize="xs" fontWeight="semibold" color="red.700" mb="8px">
            E-mails em conflito
          </Text>

          <Box
            maxH="240px"
            overflowY="auto"
            bg="white"
            borderWidth="1px"
            borderColor="red.100"
            borderRadius="md"
          >
            <Table size="sm" variant="simple">
              <Thead position="sticky" top={0} bg="gray.50" zIndex={1}>
                <Tr>
                  <Th>E-mail</Th>
                  <Th>Empresa</Th>
                </Tr>
              </Thead>
              <Tbody>
                {feedback.conflicts.map(({ email, companyName }) => (
                  <Tr key={email}>
                    <Td>{email}</Td>
                    <Td>{companyName}</Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </Box>
        </Box>
      </Box>
    );
  }

  const { summary, skipped } = feedback;

  return (
    <Box
      mt="20px"
      borderWidth="1px"
      borderColor="orange.200"
      borderRadius="md"
      overflow="hidden"
      bg="orange.50"
    >
      <Alert status="warning" variant="subtle" borderRadius={0} bg="transparent" px="16px" py="14px">
        <AlertIcon alignSelf="start" mt="2px" />
        <Box>
          <AlertTitle fontSize="sm" mb="1">
            Importação concluída com ressalvas
          </AlertTitle>
          <AlertDescription fontSize="sm">
            {summary.create} criado(s), {summary.update} atualizado(s) e {summary.remove}{' '}
            removido(s) de {summary.totalRows} linha(s). Revise as linhas ignoradas abaixo
            antes de fazer um novo envio, se necessário.
          </AlertDescription>
        </Box>
      </Alert>

      <Box px="16px" pb="16px">
        <Text fontSize="xs" fontWeight="semibold" color="orange.700" mb="8px">
          Linhas ignoradas ({skipped.length})
        </Text>

        <Box
          maxH="240px"
          overflowY="auto"
          bg="white"
          borderWidth="1px"
          borderColor="orange.100"
          borderRadius="md"
        >
          <Table size="sm" variant="simple">
            <Thead position="sticky" top={0} bg="gray.50" zIndex={1}>
              <Tr>
                <Th>Linha</Th>
                <Th>E-mail</Th>
                <Th>Nome</Th>
                <Th>Motivo</Th>
              </Tr>
            </Thead>
            <Tbody>
              {skipped.map(({ line, email, name, reason }) => (
                <Tr key={`${line}-${email ?? 'empty'}-${reason}`}>
                  <Td>{line}</Td>
                  <Td>{email ?? '—'}</Td>
                  <Td>{name ?? '—'}</Td>
                  <Td>{skipReasonLabels[reason] ?? reason}</Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </Box>
      </Box>
    </Box>
  );
}

export default function ImportMembers() {
  const {
    companyId,
    modals: { importMembers },
  } = useCompaniesContext();
  const [importFeedback, setImportFeedback] = useState<ImportFeedback | null>(null);

  const { onExportCompanyMembers, isPending: isLoading } = useExportCompanyMembers({
    companyId,
    onSuccess: (filepath: string) => {
      window.location.assign(`${config.api}/${filepath}`);
    },
  });

  const methods = useForm<FormValues>({
    mode: 'all',
    resolver: yupResolver(validationSchema) as Resolver<FormValues>,
    defaultValues,
  });

  const {
    handleSubmit,
    reset,
    setValue,
    formState: { isValid },
  } = methods;

  const toast = useToast();
  const { onSuccess, onError } = useMessages({
    success: 'Membros importados com sucesso.',
  });

  const closeModal = () => {
    reset();
    setImportFeedback(null);
    importMembers.onClose();
  };

  const { onImportCompanyRecipients, isPending } = useImportCompanyRecipients({
    companyId,
    onSuccess: ({ data }) => {
      const { create, update, remove, skipped = [], totalRows } = data;

      if (skipped.length > 0) {
        setImportFeedback({
          type: 'skipped',
          summary: { create, update, remove, totalRows },
          skipped,
        });
        toast({
          isClosable: true,
          position: 'top',
          status: 'warning',
          title: 'Importação concluída com linhas ignoradas.',
        });
        return;
      }

      onSuccess();
      closeModal();
    },
    onError: (error) => {
      const { message, conflicts } = error?.response?.data ?? {};

      if (conflicts?.length) {
        setImportFeedback({
          type: 'conflicts',
          message,
          conflicts,
        });
        toast({
          isClosable: true,
          position: 'top',
          status: 'error',
          title: 'Importação cancelada.',
        });
        return;
      }

      onError(message);
    },
  });

  const onSubmit: SubmitHandler<FormValues> = (data) => {
    setImportFeedback(null);
    onImportCompanyRecipients(data);
  };

  return (
    <Modal variant="form" isOpen={importMembers.isOpen} onClose={closeModal}>
      <ModalOverlay />

      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <ModalContent>
            <ModalHeader>
              Adicionar via planilha
              <span>Faça o upload de um arquivo XLSX. Use o modelo abaixo:</span>
            </ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <UploadFile
                onChange={(file) => {
                  setValue('file', file, { shouldValidate: true });
                  setImportFeedback(null);
                }}
                accept={{
                  'application/vnd.ms-excel': [],
                  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': [],
                }}
                description="XLSX até 5MB"
              />

              {importFeedback && <ImportFeedbackPanel feedback={importFeedback} />}
            </ModalBody>

            <ModalFooter>
              <SimpleGrid w="full" columns={importFeedback ? 3 : 2} columnGap="12px">
                <Button
                  rightIcon={<IconDownload />}
                  onClick={() => onExportCompanyMembers()}
                  isLoading={isLoading}
                >
                  Baixar planilha completa
                </Button>

                {importFeedback && (
                  <Button variant="outline" onClick={closeModal}>
                    Fechar
                  </Button>
                )}

                <SubmitButton
                  isDisabled={isPending || !isValid}
                  isLoading={isPending}
                  hideIcon
                >
                  {importFeedback?.type === 'conflicts' ? 'Tentar novamente' : 'Adicionar'}
                </SubmitButton>
              </SimpleGrid>
            </ModalFooter>
          </ModalContent>
        </form>
      </FormProvider>
    </Modal>
  );
}
